import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useRef, useState, useCallback, useEffect } from 'react';
import dayjs from 'dayjs';
import { roundTo } from '../../../../utils';

const NO_DATA_KEY = 'Blank';

export const extractHistoricValuesToData = (historicData: any[], sourceKey?: string) => {
	const result: Record<string, [number, number | null][]> = {
		[NO_DATA_KEY]: [],
	};

	if (!Array.isArray(historicData) || historicData.length === 0) {
		return result;
	}

	const actualKey = sourceKey || 'NWL';

	for (const point of historicData) {
		if (!point || typeof point.time === 'undefined') continue;

		const tsNum = Number(point.time);
		if (!Number.isFinite(tsNum)) continue;

		const tsMs = tsNum > 1e12 ? tsNum : tsNum * 1000;

		const value = point.value;
		const dataPoint: [number, number | null] = [tsMs, value];

		if (value == null) {
			result[NO_DATA_KEY].push(dataPoint);
		} else {
			if (!result[actualKey]) {
				result[actualKey] = [];
			}
			result[actualKey].push(dataPoint);
		}
	}

	return result;
};

export const extractForecastData = (forecastData) => {
	if (!Array.isArray(forecastData) || forecastData.length === 0) return [];

	const byTime = new Map<number, number[]>();

	for (const point of forecastData) {
		if (!point || typeof point.time === 'undefined') continue;

		const tsNum = Number(point.time);
		if (!Number.isFinite(tsNum)) continue;

		const tsMs = tsNum > 1e12 ? tsNum : tsNum * 1000;
		const vNum = Number(point.value);
		if (!Number.isFinite(vNum)) continue;

		if (!byTime.has(tsMs)) byTime.set(tsMs, []);
		byTime.get(tsMs)!.push(vNum);
	}

	const series: Highcharts.PointOptionsObject[] = [];

	for (const [tsMs, values] of byTime.entries()) {
		if (!values.length) {
			series.push({ x: tsMs, y: null });
			continue;
		}

		const min = Math.min(...values);
		const max = Math.max(...values);

		const worst = max;

		series.push({
			x: tsMs,
			y: worst,
			custom: { range: [min, max] },
		});
	}

	series.sort((a, b) => Number(a.x) - Number(b.x));

	return series;
};

export const formatSeriesData = (data, hslColorArr, yAxisIndex) =>
	Object.keys(data).map((source, i) => {
		const index = i % hslColorArr.length;
		const primaryColor = hslColorArr[index];
		const gradientColor = hslLightenForGradient(hslColorArr[index]);
		const readableSourceName = dataLabelLookup(source);
		const isForecast = source.toLowerCase().includes('forecast');
		const isBlank = source === NO_DATA_KEY;

		return {
			name: isBlank ? 'No data' : readableSourceName,
			type: isForecast ? 'line' : 'area',
			data: data[source],
			dashStyle: isForecast ? 'ShortDot' : 'Solid',
			lineWidth: 2,
			lineColor: isBlank ? 'rgba(0,0,0,0)' : primaryColor,
			marker: isBlank
				? { enabled: false }
				: { lineWidth: 1, lineColor: primaryColor, fillColor: gradientColor },
			fillColor:
				isBlank || isForecast
					? undefined
					: {
							linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
							stops: [
								[0, gradientColor],
								[1, gradientColor],
							],
						},
			showInLegend: !isBlank,
			enableMouseTracking: !isBlank,
			yAxis: yAxisIndex,
		};
	});

const hslStrToArr = (hslColor, alphaValue) => {
	const arr = hslColor.replace(/(\s|%|hsl|\(|\))/gim, '').split(',');
	return alphaValue ? [...arr, alphaValue] : arr;
};
const hslArrToStr = (hslColorArr) => {
	const hasAlpha = hslColorArr.length === 4;
	return `hsl${hasAlpha ? 'a' : ''}(${hslColorArr[0]},${hslColorArr[1]}%,${hslColorArr[2]}%${hasAlpha ? `,${hslColorArr[3]}` : ''})`;
};

const hslLightenForGradient = (hslColor, alphaValue?) => {
	const values = hslStrToArr(hslColor, alphaValue);
	const adjustedValue = Object.assign(
		[],
		values,
		{ 1: Number(values[1]) - 23 },
		{ 2: Number(values[2]) + 30 },
	);
	const response = hslArrToStr(adjustedValue);
	return response;
};

const dataLabelLookup = (label) => {
	const lowerCaseLabel = label.toLowerCase();
	if (lowerCaseLabel.includes('nwl')) return 'Actual data';
	if (lowerCaseLabel.includes('dt')) return 'Virtual data';
	if (lowerCaseLabel.includes('forecast')) return 'Forecast data';
	return label;
};

const generateChartData = ({
	index,
	data,
	colors,
}: {
	index: number;
	data: any;
	colors: string[];
}) => {
	const { measurementToolHistoricalValues, measurementToolForecastValues, measurementTargetName } =
		data;
	const historicData = measurementToolHistoricalValues
		? extractHistoricValuesToData(measurementToolHistoricalValues)
		: {};
	const forecastData = measurementToolForecastValues
		? extractForecastData(measurementToolForecastValues)
		: {};
	const combinedData = { ...historicData, ...forecastData };
	const series = formatSeriesData(combinedData, colors, index) as Highcharts.SeriesOptionsType[];
	return series;
};

const Chart = ({
	options,
	yAxis,
	series,
	title = '',
	height = 334,
	backgroundColor = 'rgba(0, 0, 0, 0)',
	showLegend = true,
	selectedAssetItem,
	chartCallback,
}: {
	options?: Highcharts.Options;
	yAxis?: any[];
	series?: any[];
	title?: string;
	height?: number;
	backgroundColor?: string;
	showLegend?: boolean;
	selectedAssetItem?: any;
	chartCallback?: (chart: Highcharts.Chart) => void;
}) => {
	const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
	const [chart, setChart] = useState<Highcharts.Chart | null>(null);
	const [chartOptions, setChartOptions] = useState<Highcharts.Options>();
	const chartRef = useRef<Highcharts.Chart | null>(null);

	const callback = useCallback((chart: Highcharts.Chart) => {
		if (!chartRef.current) {
			chartRef.current = chart;
			setChart(chart);
		}
		chartCallback?.(chart);
	}, []);

	useEffect(() => {
		if (options) {
			setChartOptions(options);
			return;
		}
		if (!yAxis || !series) return;
		setChartOptions({
			chart: {
				height: height,
				backgroundColor: backgroundColor,
			},
			title: {
				text: title,
			},
			xAxis: {
				crosshair: { width: 1, color: 'rgba(2,62,101,0.25)' },
				labels: {
					formatter: ({ value, isFirst, isLast }) =>
						isFirst || isLast ? dayjs(value).format('D MMM HH:mm') : dayjs(value).format('HH:mm'),
				},
			},
			yAxis,
			series,
			credits: {
				enabled: false,
			},
			legend: {
				enabled: showLegend,
			},
			exporting: {
				enabled: false,
			},
			tooltip: {
				shared: true,
				split: false,
				useHTML: true,
				backgroundColor: '#ffffff',
				borderColor: 'rgba(0,0,0,0.25)',
				borderRadius: 8,
				shadow: true,
				padding: 10,
				style: { color: '#111827', fontSize: '12px', fontWeight: '600' },
				formatter: function () {
					const when = dayjs(Number(this.x)).format('D MMM YYYY HH:mm');
					const rows = (this.points ?? [])
						.map((pt) => {
							const y = Number(pt.y);

							const uo: any = (pt.series && (pt.series.userOptions as any)) ?? {};
							const cus = uo.custom ?? {};

							const rawName = pt.series?.name || '';
							const [fallbackAsset, fallbackParam] = rawName.split(' — ');
							const assetName = cus.assetName ?? fallbackAsset ?? rawName;
							const paramLabel = cus.paramLabel ?? fallbackParam ?? '';

							let unit = cus.unit ?? '';
							if (
								!unit &&
								pt.series?.yAxis?.options?.title &&
								typeof pt.series.yAxis.options.title.text === 'string'
							) {
								const m = /\(([^)]+)\)/.exec(pt.series.yAxis.options.title.text);
								if (m) unit = m[1];
							}
							const chipColor =
								cus.colorChip ??
								(pt.series && (pt.series as any).color) ??
								(pt as any).color ??
								(pt.series && (pt.series.userOptions as any)?.lineColor) ??
								'#999';

							const value = Number.isFinite(y) ? roundTo(y, 3) : y;
							const range = (pt.point as any)?.custom?.range;
							const hasRange =
								Array.isArray(range) &&
								range.length === 2 &&
								Number.isFinite(range[0]) &&
								Number.isFinite(range[1]);

							const valueHtml = hasRange
								? `<div style="font-weight:800;color:#0A8399;font-size:14px;">
									${paramLabel || pt.series.name}: ${Number(range[0]).toFixed(3)} – ${Number(range[1]).toFixed(3)}${unit ? ` ${unit}` : ''}
									</div>`
								: `<div style="font-weight:800;color:#0A8399;font-size:14px;">
									${paramLabel || pt.series.name}: ${value}${unit ? ` ${unit}` : ''}
									</div>`;

							return `
                                <div style="display:flex;align-items:flex-start;gap:10px;margin:6px 0;">
                                <span style="display:inline-block;width:15px;height:15px;border-radius:3px;margin-top:3px;background:${chipColor};"></span>
                                <div>
                                    <div style="font-weight:800;color:#0A8399;font-size:14px;">
                                    ${valueHtml}
                                    </div>
                                    <div style="font-weight:500;font-size:12px;color:#023E65;">${assetName}</div>
                                </div>
                                </div>
                            `;
						})
						.join('');

					return `
                            <div style="min-width:200px;line-height:1.4;font-weight:600;">
                                <div style="color:#374151;font-size:13px;margin-bottom:6px">${when}</div>
                                ${rows}
                            </div>
                            `;
				},
			},
		});
	}, [series, yAxis, options]);
	return (
		<div>
			{(series ?? options) && (
				<HighchartsReact
					highcharts={Highcharts}
					options={chartOptions}
					callback={callback}
					ref={chartComponentRef}
				/>
			)}
		</div>
	);
};

export default Chart;
export { generateChartData };
